import { Checkbox, Divider, Stack, TagsInput, Text } from '@mantine/core';
import { schemaResolver, useForm } from '@mantine/form';
import { Panel } from 'components/Panel';
import { SaveButton } from 'components/SaveButton';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { useProfile } from 'init/use-enforce-profile';
import { i18nKeys, useTranslation } from 'locales';
import { useEffect } from 'react';
import { useLoadLightUsers } from 'shared/hooks/use-load-light-users';
import {
  type EmailSendingPreferences,
  EmailSendingPreferencesSchema,
  useLoadEmailSendingPreferences,
  useUpdateEmailSendingPreferences,
} from '../api';
import { EmailDomainSection } from './EmailDomainSection';

const INITIAL_VALUES: EmailSendingPreferences = {
  sendCopy: false,
  sendCopyEmails: [],
  sendToAllEmails: false,
  sendToContactPersonAndDebtorEmail: false,
  defaultFrom: '',
};

const SectionTitle = ({ children }: { children: string }) => (
  <Text size="xs" fw={600} c="dimmed" tt="uppercase" lts="0.06em">
    {children}
  </Text>
);

export const SendingPreferencesCard = () => {
  const { t } = useTranslation();

  const company = useCompanyConfiguration();
  const profile = useProfile();
  const canSeeUserSuggestions = profile.isAdmin || profile.roles.includes('all_invoices');
  const { users = [] } = useLoadLightUsers(
    false,
    { query: '' },
    { isEnabled: canSeeUserSuggestions },
  );

  const { emailSendingPreferences } = useLoadEmailSendingPreferences();
  const { updateEmailSendingPreferences, isUpdateEmailSendingPreferencesLoading } =
    useUpdateEmailSendingPreferences();

  const form = useForm<EmailSendingPreferences>({
    initialValues: INITIAL_VALUES,
    validate: schemaResolver(EmailSendingPreferencesSchema, { sync: true }),
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: sync the loaded preferences into the form only when the fetched data changes (the form object identity is unstable)
  useEffect(() => {
    if (!emailSendingPreferences) return;

    form.setInitialValues(emailSendingPreferences);
    form.setValues(emailSendingPreferences);
  }, [emailSendingPreferences]);

  const handleSave = () =>
    updateEmailSendingPreferences(form.getValues(), { onSuccess: () => form.resetDirty() });

  return (
    <Panel
      title={t(i18nKeys.FORM.PREFERENCES.SENDING_PREFERENCES.TITLE)}
      withBorder
      footer={
        <SaveButton
          onClick={handleSave}
          disabled={!form.isDirty()}
          loading={isUpdateEmailSendingPreferencesLoading}
          hideIcon
        />
      }
    >
      <Stack gap="xl" my="md">
        <Stack gap="sm">
          <SectionTitle>
            {t(i18nKeys.FORM.PREFERENCES.SENDING_PREFERENCES.COPY_SECTION)}
          </SectionTitle>
          <Checkbox
            label={t(i18nKeys.FORM.PREFERENCES.SENDING_PREFERENCES.SEND_COPY)}
            description={t(i18nKeys.FORM.PREFERENCES.SENDING_PREFERENCES.SEND_COPY_HINT)}
            {...form.getInputProps('sendCopy', { type: 'checkbox' })}
          />
          {form.getValues().sendCopy && (
            <TagsInput
              maw={420}
              ml="xl"
              label={t(i18nKeys.FORM.PREFERENCES.SENDING_PREFERENCES.SEND_COPY_EMAILS)}
              description={t(i18nKeys.FORM.PREFERENCES.SENDING_PREFERENCES.SEND_COPY_EMAILS_HINT)}
              data={users.map(user => user.email)}
              {...form.getInputProps('sendCopyEmails')}
            />
          )}
        </Stack>

        <Stack gap="sm">
          <SectionTitle>
            {t(i18nKeys.FORM.PREFERENCES.SENDING_PREFERENCES.RECIPIENTS_SECTION)}
          </SectionTitle>
          <Checkbox
            label={t(i18nKeys.FORM.PREFERENCES.SENDING_PREFERENCES.SEND_TO_ALL_EMAILS)}
            description={t(i18nKeys.FORM.PREFERENCES.SENDING_PREFERENCES.SEND_TO_ALL_EMAILS_HINT)}
            {...form.getInputProps('sendToAllEmails', { type: 'checkbox' })}
          />
          <Checkbox
            label={t(
              i18nKeys.FORM.PREFERENCES.SENDING_PREFERENCES.SEND_TO_CONTACT_PERSON_AND_DEBTOR_EMAIL,
            )}
            description={t(
              i18nKeys.FORM.PREFERENCES.SENDING_PREFERENCES
                .SEND_TO_CONTACT_PERSON_AND_DEBTOR_EMAIL_HINT,
            )}
            {...form.getInputProps('sendToContactPersonAndDebtorEmail', { type: 'checkbox' })}
          />
        </Stack>

        <Divider />

        <EmailDomainSection
          form={form}
          canUseCustomDomain={company.package.can_use_custom_domain}
        />
      </Stack>
    </Panel>
  );
};
